import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from 'recharts';
import colors from '../../../config/colors';
import Typography from '../../../components/shared/Typography/Typography.component';
import { i18nService } from '../../../services/i18n.service';
import { formatAsHHMM } from '../../../models/duration.model';
import { HomePageMonthlyHoursDataResponse } from '../HomePage.container';

const MONTH_KEY_BY_NUMBER: Record<number, string> = {
	1: 'months.January',
	2: 'months.February',
	3: 'months.March',
	4: 'months.April',
	5: 'months.May',
	6: 'months.June',
	7: 'months.July',
	8: 'months.August',
	9: 'months.September',
	10: 'months.October',
	11: 'months.November',
	12: 'months.December',
};

const CustomXTick = ({ x, y, payload }: any) => (
	<text
		x={x}
		y={y}
		dy={14}
		textAnchor='middle'
		fontSize={12}
		fill={colors.primaryDarkBlue}
		fontFamily='RubikMedium'
	>
		{i18nService.translate(payload.value)}
	</text>
);

const CustomYTick = ({ x, y, payload }: any) => (
	<text
		x={x}
		y={y}
		dx={-4}
		dy={4}
		textAnchor='end'
		fontSize={12}
		fill={colors.primaryDarkBlue}
		fontFamily='RubikMedium'
	>
		{payload.value}
	</text>
);

const CustomTooltip = ({ active, payload }: any) => {
	if (active && payload && payload.length) {
		const { monthKey, year, totalMinutes } = payload[0].payload;
		return (
			<div
				style={{
					backgroundColor: '#fff',
					borderRadius: 6,
					padding: '12px',
					boxShadow: '2px 4px 8px 0 rgba(0,0,0,0.16)',
					display: 'flex', 
					flexDirection: 'column',
					gap: 4,
					minWidth: 120,
					direction: 'rtl',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							backgroundColor: colors.datepickerChooseBG,
							display: 'inline-block',
							flexShrink: 0,
						}}
					/>
					<span
						style={{
							fontFamily: 'RubikRegular',
							fontSize: 14,
							fontWeight: 400,
							color: colors.primaryDarkBlue,
						}}
					>
						{`${formatAsHHMM(totalMinutes * 60)} שעות`}
					</span>
				</div>
				<span
					style={{
						fontFamily: 'RubikRegular',
						fontSize: 12,
						color: colors.tooltipPercentage,
						//paddingRight: 18,
					}}
				>
					{`חודש ${i18nService.translate(monthKey)} ${year}`}
				</span>
			</div>
		);
	}
	return null;
};

const MonthlyHoursChart = ({ monthlyHoursData }: { monthlyHoursData: HomePageMonthlyHoursDataResponse[] | null }) => {
	const chartData = (monthlyHoursData ?? []).map(d => ({
		monthKey: MONTH_KEY_BY_NUMBER[d.month],
		year: d.year,
		hours: d.total / 60,
		totalMinutes: d.total,
	}));

	return (
		<View style={styles.card}>
			<Typography
				size={14}
				font='medium'
				color='primaryDarkBlue'
				style={styles.title}
			>
				{i18nService.translate('chart.monthlyHours')}
			</Typography>
			<div style={{ width: '100%', height: 200 }}>
				<ResponsiveContainer width='100%' height='100%'>
					<AreaChart
						data={chartData}
						margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
					>
						<defs>
							<linearGradient id='areaGradient' x1='0' y1='0' x2='0' y2='1'>
								<stop offset='0%' stopColor={colors.subMenuBG} stopOpacity={1} />
								<stop offset='100%' stopColor={colors.subMenuBG} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid
							vertical={true}
							horizontal={false}
							stroke={colors.divider}
						/>
						<XAxis
							dataKey='monthKey'
							tickLine={false}
							axisLine={true}
							interval={0}
							tick={<CustomXTick />}
						/>
						<YAxis
							dataKey='hours'
							tickLine={false}
							axisLine={true}
							domain={[0, 250]}
							ticks={[0, 100, 200]}
							tick={<CustomYTick />}
						/>
						<Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
						<Area
							type='monotone'
							dataKey='hours'
							stroke={colors.titlePrimary}
							strokeWidth={2}
							fill='url(#areaGradient)'
							dot={{ r: 2, fill: colors.titlePrimary }}
							activeDot={{ r: 4 }}
						/>
					</AreaChart>
				</ResponsiveContainer>
			</div>
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		flex: 1,
		backgroundColor: colors.white,
		borderRadius: 8,
		padding: 24,
		gap: 16
	},
	title: {
		textAlign: 'right',
	},
});

export default MonthlyHoursChart;
